import mergeClasses from '../../../utils/shallowMerge';
import clsx from 'clsx';
import { Link } from '../../atoms/link/Link';
import { Text } from '../../typography/text/Text';
import defaultClasses from './CategoryTile.module.scss';
import PropTypes from 'prop-types';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { ResponsiveImage } from '../../atoms/image/ResponsiveImage';
import { StylesCustomizer } from '../stylesCustomizer';
import VideoPlayer from '../../atoms/VideoPlayer';
import { Image } from '../../atoms/image/Image';
import { useMedia } from 'react-use';

const CategoryTile = ({
  titleLink = null,
  title = '',
  titleStyles,
  description = '',
  descriptionStyles,
  image = '',
  mobileImage,
  links = null,
  isFeatured = false,
  imageRef = null,
  customClasses,
  linkObj,
  style,
  align = 'left',
  tileLayout,
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const { ref: mediaRef, inView } = useInView();
  const isMobile = useMedia('(max-width: 680px)', false);

  const TitleText = (variables) => {
    return (
      <Link
        link={{ reference: titleLink }}
        className={styles.linkTitle}
        variables={variables}
      >
        <Text
          as="span"
          weight="light"
          align={align}
          className={styles.title}
          variables={variables}
        >
          {title}
        </Text>
      </Link>
    );
  };
  const DescText = (variables) => {
    return (
      <Text
        as="p"
        size="s"
        align={align}
        className={styles.text}
        variables={variables}
      >
        {description}
      </Text>
    );
  };

  const StyledTitleText = StylesCustomizer(TitleText);
  const StyledDescText = StylesCustomizer(DescText);
  const renderMedia = React.useMemo(
    () => (
      <div className={styles.image}>
        {image?.media?.resourceType ? (
          image.media.resourceType === 'video' ? (
            <VideoPlayer
              url={image.media.file}
              aspectRatio={image?.ratio?.replace(':', '/')}
              style={{
                height: 'auto',
                position: 'absolute',
              }}
            />
          ) : image?.media?.file?.includes('/media.')
            ? (
              <ResponsiveImage
                mobileSource={mobileImage}
                source={image}
                loading={inView ? 'eager' : 'lazy'}
                className={styles.imageTile}
              />
            )
            : (
              <Image
                alt={image?.name}
                title={image?.name}
                itemProp="image"
                className={styles.imageTile}
                image={(isMobile && mobileImage?.media?.file) ? mobileImage.media.file : image?.media?.file}
              />
            )
        ) : null}
      </div>
    ),
    [image, inView],
  );

  return (
    <div
      style={style}
      className={clsx(
        styles.categoryTileWrapper,
        styles[`categoryTitle_${tileLayout}`],
      )}
      data-featured={isFeatured}
    >
      <div ref={mediaRef}>
        <Link
          link={{
            reference: linkObj,
          }}
          className={`${styles.imagelink} ${
            isFeatured ? styles.isFeatured : ''
          }`}
          ref={imageRef}
        >
          {renderMedia}
        </Link>
      </div>

      {title || description || links?.length ? (
        <div
          className={clsx(
            styles.categoryTileContent,
            styles[`categoryTitleAlign_${align}`],
            styles[`categoryTitleLayout_${tileLayout}`],
          )}
        >
          {title && <StyledTitleText styles={titleStyles} />}
          {description && <StyledDescText styles={descriptionStyles} />}
          {links && links.length ? (
            <div
              className={clsx(
                styles.linkContainer,
                styles[`linkContainerLayout_${tileLayout}`],
                {
                  [styles.linkContainerGapOne]: links.length === 1,
                  [styles.linkContainerGap]: links.length > 1,
                },
              )}
            >
              {links.map(({ title, subCatUrl }, index) => (
                <Link
                  key={index}
                  link={{ reference: subCatUrl }}
                  className={styles.link}
                  dataButtonType="nodeLink"
                >
                  <Text as="span" weight="light" size="xs">
                    {title}
                  </Text>
                </Link>
              ))}
            </div>
          ) : (
            <div className={styles.linkPlaceholder}>placeholder</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

CategoryTile.propTypes = {
  image: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  titleLink: PropTypes.object,
  links: PropTypes.array,
  isFeatured: PropTypes.bool,
  imageRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]),
};

export default CategoryTile;
